import { useCallback, useState, type FormEvent } from 'react';
import { SendHorizontal, X } from 'lucide-react';
import { Avatar, Card, IconButton, cn, toast } from '@cyphex/ui';
import type { ConversationId } from '@cyphex/types';
import { useAuth } from '@/features/auth/hooks/useAuth';
import { chatApi } from '@/features/chat/api';
import { ACTIVITY_META } from '@/features/presence/presenceMeta';
import { usePersonSnapshot } from '@/features/presence/hooks/usePresence';
import { presenceTitle } from '@/features/presence/lastSeen';
import { absoluteTime, isoTime, relativeTime } from '../relativeTime';
import type { MessagePopupRow } from '../popupStore';

export interface MessagePopupCardProps {
  rows: readonly MessagePopupRow[];
  onOpen: (row: MessagePopupRow) => void;
  onDismiss: (id: string) => void;
  onViewAll: () => void;
  onPauseChange: (paused: boolean) => void;
}

/** `/chat/<conversationId>?m=<messageId>` — what a quick reply is aimed at. */
function conversationOf(deepLink: string | undefined): ConversationId | null {
  const match = /^\/chat\/([0-9a-fA-F-]{36})/.exec(deepLink ?? '');
  return match?.[1] ? (match[1] as ConversationId) : null;
}

function PopupRow({
  row,
  onOpen,
  onDismiss,
}: {
  row: MessagePopupRow;
  onOpen: (row: MessagePopupRow) => void;
  onDismiss: (id: string) => void;
}) {
  const snapshot = usePersonSnapshot(row.actorId);
  const presence = snapshot?.effectiveState ?? 'offline';

  return (
    <div className="group/row relative">
      <button
        type="button"
        onClick={() => onOpen(row)}
        className={cn(
          'flex w-full items-start gap-3 rounded-xl px-3 py-2.5 text-left transition-colors duration-fast',
          'hover:bg-subtle focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
        )}
      >
        <Avatar
          name={row.name}
          src={row.avatarUrl}
          size="xl"
          presence={ACTIVITY_META[presence].dot}
          /* The popup is a dot and two lines of message; the dot is therefore
             the only presence it can state, so it states all of it. */
          presenceLabel={presenceTitle(
            presence,
            snapshot?.resolved === true ? snapshot.lastActivityAt : null,
          )}
          presenceRingClassName="ring-card"
        />

        <span className="min-w-0 flex-1">
          <span className="flex items-baseline gap-2">
            <span className="min-w-0 flex-1 truncate text-md font-semibold text-foreground">
              {row.name}
            </span>
            <time
              dateTime={isoTime(row.createdAt)}
              title={absoluteTime(row.createdAt)}
              className="shrink-0 text-sm tabular text-muted-foreground"
            >
              {relativeTime(row.createdAt)}
            </time>
          </span>
          <span className="mt-0.5 line-clamp-2 block text-md leading-snug text-muted-foreground">
            {row.preview}
          </span>
        </span>
      </button>

      {/* Sits over the row rather than in it, so the text never reflows on hover. */}
      <span className="absolute right-1.5 top-1.5 opacity-0 transition-opacity duration-fast group-hover/row:opacity-100 focus-within:opacity-100">
        <IconButton
          icon={X}
          label={`Dismiss notification from ${row.name}`}
          size="xs"
          variant="ghost"
          onClick={() => onDismiss(row.id)}
        />
      </span>
    </div>
  );
}

/**
 * The message notification, in the shape Teams uses, on our own surface.
 *
 * Two things in that shape are load-bearing rather than decorative:
 *
 *   • THE ACCOUNT LINE — which identity, which workspace. That is the one piece
 *     of context the card cannot get from the message itself, and the only
 *     reason an app name belongs on a notification at all. It used to read
 *     "Cyphex Teams", which told you nothing the window behind it did not.
 *
 *   • THE QUICK REPLY — most notifications want a one-line answer, and clicking
 *     through to a thread to type "on my way" is three steps too many.
 *
 * The surface stays CARD WHITE. A notification has to be distinct from the page,
 * but it earns that with elevation, a hairline and a brand accent on the one
 * control you press — not by flooding 360px with saturated colour, which reads
 * as a banner ad rather than as a message from a colleague.
 */
export function MessagePopupCard({
  rows, onOpen, onDismiss, onViewAll, onPauseChange,
}: MessagePopupCardProps) {
  const { user, tenant } = useAuth();
  const head = rows[0] ?? null;
  const [reply, setReply] = useState('');
  const [sending, setSending] = useState(false);
  const conversationId = conversationOf(head?.deepLink);

  const onSubmit = useCallback(
    async (event: FormEvent) => {
      event.preventDefault();
      const text = reply.trim();
      if (text.length === 0 || conversationId === null || head === null || sending) return;
      setSending(true);
      try {
        await chatApi.sendMessage(conversationId, {
          clientMsgId: crypto.randomUUID(),
          body: { type: 'doc', content: [{ type: 'paragraph', content: [{ type: 'text', text }] }] },
        });
        setReply('');
        onDismiss(head.id);
      } catch {
        /* The card is about to disappear, so the failure is reported where the
           user is looking — and their words are kept, not swallowed. */
        toast.error('Could not send. Open the chat and try again.');
      } finally {
        setSending(false);
      }
    },
    [reply, conversationId, head, sending, onDismiss],
  );

  if (head === null) return null;

  const canSend = reply.trim().length > 0 && !sending;

  return (
    <Card
      as="section"
      padding="none"
      aria-label="New messages"
      className={cn(
        'w-[360px] max-w-[calc(100vw-2rem)] overflow-hidden rounded-2xl',
        /* A white card on a white app disappears. Definition comes from a real
           border plus a deep, wide shadow — the same way a physical card reads
           on white paper — rather than from a coloured stripe. */
        'border-2 border-foreground/25 bg-card animate-slide-in-right',
        'shadow-[0_16px_40px_-12px_rgb(0_0_0/0.28),0_4px_12px_-4px_rgb(0_0_0/0.16)]',
      )}
      onMouseEnter={() => onPauseChange(true)}
      onMouseLeave={() => onPauseChange(false)}
      onFocusCapture={() => onPauseChange(true)}
      onBlurCapture={() => onPauseChange(false)}
    >
      <header className="flex items-center gap-2 px-3.5 pb-1 pt-2.5">
        {/* WORKSPACE FIRST, and it never truncates. Which organisation a
            message landed in is the thing you cannot infer from the message;
            your own address you already know. With the email leading, the
            company name was the half that got cut off — exactly backwards. */}
        <span className="flex min-w-0 flex-1 items-baseline gap-1.5">
          <span className="shrink-0 text-sm font-semibold text-foreground">
            {tenant?.name ?? ''}
          </span>
          {user?.email ? (
            <span className="min-w-0 truncate text-sm text-muted-foreground">· {user.email}</span>
          ) : null}
        </span>
        <IconButton
          icon={X}
          label="Dismiss all notifications"
          size="xs"
          variant="ghost"
          onClick={() => rows.forEach((row) => onDismiss(row.id))}
        />
      </header>

      <div aria-live="polite" className="px-1.5 pb-1">
        {rows.map((row) => (
          <PopupRow key={row.id} row={row} onOpen={onOpen} onDismiss={onDismiss} />
        ))}
      </div>

      {conversationId === null ? (
        <div className="border-t border-border px-3.5 py-2.5">
          <button
            type="button"
            onClick={onViewAll}
            className="rounded text-md font-semibold text-link underline-offset-2 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            View all messages
          </button>
        </div>
      ) : (
        <form onSubmit={onSubmit} className="border-t border-border px-3 py-2.5">
          <div
            className={cn(
              'flex items-center gap-1.5 rounded-xl border border-foreground/25 bg-subtle pl-3 pr-1.5 transition-colors',
              'focus-within:border-primary focus-within:bg-card',
            )}
          >
            <input
              value={reply}
              onChange={(event) => setReply(event.target.value)}
              placeholder={`Reply to ${head.name.split(' ')[0] ?? head.name}`}
              aria-label={`Quick reply to ${head.name}`}
              disabled={sending}
              className="min-w-0 flex-1 bg-transparent py-2 text-md text-foreground placeholder:text-muted-foreground focus:outline-none disabled:opacity-60"
            />
            <button
              type="submit"
              aria-label="Send reply"
              disabled={!canSend}
              className={cn(
                'my-1 flex h-7 w-7 shrink-0 items-center justify-center rounded-lg transition-colors duration-fast',
                'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                canSend
                  ? 'bg-primary text-primary-foreground hover:opacity-90'
                  : 'bg-transparent text-muted-foreground',
              )}
            >
              <SendHorizontal size={15} aria-hidden />
            </button>
          </div>
        </form>
      )}
    </Card>
  );
}
